import type { ComponentType } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowRotateLeft,
  faBoxesStacked,
  faCalendarCheck,
  faCashRegister,
  faChartPie,
  faClockRotateLeft,
  faClipboardList,
  faCoins,
  faCreditCard,
  faCube,
  faFileContract,
  faFileInvoiceDollar,
  faGaugeHigh,
  faHandshake,
  faListCheck,
  faPenToSquare,
  faRightLeft,
  faScaleBalanced,
  faSitemap,
  faScrewdriverWrench,
  faShoppingCart,
  faSliders,
  faWarehouse,
  faReceipt,
  faWallet,
  faUsersGear,
  faUserTie,
  faPeopleGroup,
  faUserCheck,
} from '@fortawesome/free-solid-svg-icons';

const createIconComponent = (
  icon: IconDefinition,
  displayName: string,
): ComponentType<Omit<FontAwesomeIconProps, 'icon'>> => {
  const WrappedIcon = (props: Omit<FontAwesomeIconProps, 'icon'>) => (
    <FontAwesomeIcon icon={icon} fixedWidth focusable="false" {...props} />
  );
  WrappedIcon.displayName = displayName;
  return WrappedIcon;
};

export const ERP_ROUTE_ICONS = {
  'erp.overview': createIconComponent(faGaugeHigh, 'ErpOverviewIcon'),
  'erp.sales.pos': createIconComponent(faCashRegister, 'ErpSalesPosIcon'),
  'erp.orders.list': createIconComponent(faListCheck, 'ErpOrdersListIcon'),
  'erp.orders.carts': createIconComponent(faShoppingCart, 'ErpOrdersCartsIcon'),
  'erp.orders.returns': createIconComponent(faArrowRotateLeft, 'ErpOrdersReturnsIcon'),
  'erp.inventory': createIconComponent(faBoxesStacked, 'ErpInventoryIcon'),
  'erp.inventory.moves': createIconComponent(faRightLeft, 'ErpInventoryMovesIcon'),
  'erp.inventory.adjustments': createIconComponent(
    faScrewdriverWrench,
    'ErpInventoryAdjustmentsIcon',
  ),
  'erp.inventory.byWarehouse': createIconComponent(
    faWarehouse,
    'ErpInventoryByWarehouseIcon',
  ),
  'erp.purchasing.orders': createIconComponent(
    faFileInvoiceDollar,
    'ErpPurchasingOrdersIcon',
  ),
  'erp.suppliers': createIconComponent(faHandshake, 'ErpSuppliersIcon'),
  'erp.services.agenda': createIconComponent(faCalendarCheck, 'ErpServicesAgendaIcon'),
  'erp.services.group_classes': createIconComponent(faPeopleGroup, 'ErpServicesGroupClassesIcon'),
  'erp.services.catalogue': createIconComponent(
    faClipboardList,
    'ErpServicesCatalogueIcon',
  ),
  'erp.services.itemEditor': createIconComponent(
    faPenToSquare,
    'ErpServicesItemEditorIcon',
  ),
  'erp.services.spaces': createIconComponent(faSitemap, 'ErpServicesSpacesIcon'),
  'erp.services.professionals': createIconComponent(faUserTie, 'ErpServicesProfessionalsIcon'),
  'erp.services.history': createIconComponent(
    faClockRotateLeft,
    'ErpServicesHistoryIcon',
  ),
  'erp.expenses': createIconComponent(faReceipt, 'ErpExpensesIcon'),
  'erp.expenses.paymentMethods': createIconComponent(
    faCreditCard,
    'ErpExpensesPaymentMethodsIcon',
  ),
  'erp.finance.balances': createIconComponent(
    faScaleBalanced,
    'ErpFinanceBalancesIcon',
  ),
  'erp.cashflow.balance': createIconComponent(
    faWallet,
    'ErpCashflowBalanceIcon',
  ),
  'erp.expenses.closings': createIconComponent(faCoins, 'ErpExpensesClosingsIcon'),
  'erp.configuration.parameters': createIconComponent(
    faSliders,
    'ErpConfigurationParametersIcon',
  ),
  'erp.configuration.commissions': createIconComponent(
    faChartPie,
    'ErpConfigurationCommissionsIcon',
  ),
  'erp.configuration.roles': createIconComponent(
    faUsersGear,
    'ErpConfigurationRolesIcon',
  ),
  'erp.customers.overview': createIconComponent(
    faPeopleGroup,
    'CrmCustomersOverviewIcon',
  ),
  'erp.subscriptions.plans': createIconComponent(
    faFileContract,
    'ErpSubscriptionsPlansIcon',
  ),
  'erp.subscriptions.customers': createIconComponent(
    faUserCheck,
    'ErpSubscriptionsCustomersIcon',
  ),
  default: createIconComponent(faCube, 'ErpDefaultIcon'),
} as const satisfies Record<string, ComponentType<Omit<FontAwesomeIconProps, 'icon'>>>;

export type ErpRouteIconKey = keyof typeof ERP_ROUTE_ICONS;

export const getErpRouteIcon = (
  key: string,
): ComponentType<Omit<FontAwesomeIconProps, 'icon'>> =>
  ERP_ROUTE_ICONS[key as ErpRouteIconKey] ?? ERP_ROUTE_ICONS.default;
